// How a shopper reaches the store: its email, phone and address as the merchant keeps them under
// Settings, and the links that open each one. A contact page binds these rather than typing them, so a
// change in Settings reaches every page, and a theme's sample address never shows on a real store.

/** How the store is reached, each part empty while the merchant has not filled it in. */
export type ContactFacts = { email: string; phone: string; address: string };

/** The sources that speak for how the store is reached, which a live page leaves out while they are empty. */
export const STORE_DETAILS = [
  'store.email', 'store.phone', 'store.address', 'store.emailLink', 'store.phoneLink', 'store.mapLink'
] as const;

/** How each store detail is answered from the store's contact facts. */
const DETAIL_FACTS: Readonly<Record<(typeof STORE_DETAILS)[number], (contact: ContactFacts) => string>> = {
  'store.email': (contact) => contact.email,
  'store.phone': (contact) => contact.phone,
  'store.address': (contact) => contact.address,
  'store.emailLink': (contact) => (contact.email ? `mailto:${contact.email}` : ''),
  'store.phoneLink': (contact) => (contact.phone ? `tel:${contact.phone.replace(/[^\d+]/g, '')}` : ''),
  'store.mapLink': (contact) =>
    (contact.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}` : '')
};

// Reports whether a source speaks for how the store is reached.
export const isStoreDetail = (source: string): source is (typeof STORE_DETAILS)[number] =>
  (STORE_DETAILS as readonly string[]).includes(source);

// Reads one setting as trimmed words, or nothing when the merchant left it out or it is not words.
function settingText(settings: Record<string, unknown>, key: string): string {
  const value = settings[key];
  return typeof value === 'string' ? value.trim() : '';
}

// Reads how the store is reached out of its settings, the street and the city joined into one address.
export function contactOf(settings: unknown): ContactFacts {
  const bag = settings !== null && typeof settings === 'object' ? (settings as Record<string, unknown>) : {};
  const address = [settingText(bag, 'address1'), settingText(bag, 'city')].filter(Boolean).join(', ');
  return { email: settingText(bag, 'contactEmail'), phone: settingText(bag, 'contactPhone'), address };
}

// Answers a store detail, or nothing when the page was drawn without the store's settings to read.
export function contactFact(source: (typeof STORE_DETAILS)[number], contact: ContactFacts | undefined): string | undefined {
  return contact ? DETAIL_FACTS[source](contact) : undefined;
}
